// R27-3 — LALA'S BOOKSHELF, her own three books, and it comes out carrying W5-2's decision.
//
// W5-2's ruling was that SAVE TO MY ROOM lives on EVERY book: a chip on all 98 category spines, a
// persistent chip on each of her three covers here, and the full sentence in both readers. The chip
// here is behind `onSave && isSaved` — BOTH, because a save button with no way to know whether it is
// already saved renders a toggle that lies about its own state.
//
// That guard is the R26 shape in miniature and it is why this is worth a gate rather than a grep: the
// capability is OPTIONAL, so a second call site added without those two props would draw the shelf
// with no save on it and nothing would throw, nothing would log, and an absent chip has no error
// state (R25). There is exactly one call site today and it passes both. The check is written as the
// law — given the pair, every cover carries a chip; given neither, the covers still render — so the
// next surface that shows her books is held to it rather than having to be remembered.
//
// The cover is the catalogue's own bytes (`/api/library/cover/:slug`) and falls back to the TITLE set
// as a plate rather than to a broken image or an empty slot: a book with no cover is still a book she
// can open, and W5-2's whole point was that one of her three being a dead end is worse than plain.
//
// Props in, tree out, no hook (R7/R8), so `scripts/r27-featured-tier.mjs` CALLS it.

import { SaveToRoomButton } from "./SaveToRoomButton";
import { DEFAULT_LANGUAGE } from "../language.js";
import type { FeaturedBook } from "../lib/shelves";

// 14-B7d(2): a DEFAULT, because r27-featured-tier.mjs CALLS this shelf with four props and no more.
// Her three books keep their own names; the chip beside them takes her language.
export function FeaturedShelf({ books, onOpen, isSaved, onSave, language = DEFAULT_LANGUAGE }: {
  books: FeaturedBook[];
  onOpen: (book: FeaturedBook) => void;
  // W5-2: her three books offer Save to My Room ON THE COVER, like every other book.
  isSaved?: (slug: string) => boolean | null;
  onSave?: (slug: string) => void;
  language?: string;
}) {
  return (
    <section className="shelf-row shelf-row--featured" aria-label="Lala's Bookshelf">
      <div className="shelf-row__label shelf-row__label--featured">
        <span>Lala&rsquo;s Bookshelf</span>
      </div>
      <div className="shelf-row__unit">
        <div className="shelf-row__books shelf-row__books--featured">
          {books.map((book) => (
            <div key={book.slug} className="featured-book-slot">
              <button type="button" className="featured-book" data-slug={book.slug} onClick={() => onOpen(book)}>
                {book.hasCover ? (
                  <img className="featured-book__cover" src={`/api/library/cover/${book.slug}`} alt={book.title} loading="lazy" />
                ) : (
                  <span className="featured-book__cover featured-book__cover--placeholder">{book.title}</span>
                )}
                <span className="featured-book__title">{book.title}</span>
                {book.author ? <span className="featured-book__author">{book.author}</span> : null}
              </button>
              {onSave && isSaved ? (
                <SaveToRoomButton slug={book.slug} title={book.title} saved={isSaved(book.slug)} onToggle={onSave} variant="shelf" className="shelf-save--cover" language={language} />
              ) : null}
            </div>
          ))}
        </div>
        <div className="shelf-plank shelf-plank--featured" aria-hidden="true">
          <span className="shelf-plank__finial shelf-plank__finial--l">❖</span>
          <span className="shelf-plank__finial shelf-plank__finial--r">❖</span>
        </div>
      </div>
    </section>
  );
}
